// hooks/market/useMarketSearch.ts
//
// Recherche libre par nom, sur tout le catalogue — boutiques et produits
// (barre de recherche du Market, écrans index.tsx et boutiques.tsx).
//
// - useSearchShops(name)               : boutiques publiées dont le nom
//   contient le terme recherché (ILIKE, insensible à la casse), paginé.
// - useSearchProducts(name, categoryId?) : produits publiés dont le nom
//   contient le terme recherché, sur TOUT le catalogue (name obligatoire
//   côté backend), avec un filtre de catégorie optionnel additionnel —
//   distinct de useProductsByCategory (voir useMarketCategories.ts) où
//   c'est l'inverse : category_id obligatoire, name optionnel. Les deux
//   coexistent pour deux usages différents : ici une recherche libre que
//   l'utilisateur peut ensuite affiner par catégorie ; là-bas une
//   navigation qui part toujours d'une catégorie choisie.
//
// Les deux hooks partagent le même comportement : se rechargent depuis le
// début à chaque changement de terme (ou de catégorie pour les produits)
// — évite de mélanger des résultats de recherches différentes — et ne
// déclenchent aucun appel réseau tant que `name` est vide (pas de
// recherche "tout le catalogue" implicite sur un champ non rempli).
//
// Appelle le vrai backend :
//   POST {API_BASE_URL}/market   action: 'search-shops',    { access_token, name, limit?, offset? }
//   POST {API_BASE_URL}/market   action: 'search-products', { access_token, name, category_id?, limit?, offset? }
// (voir backend/handleMarket.ts → searchShops / searchProducts)

import { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '@/lib/api';
import { getValidToken } from '@/lib/auth';
import type { MarketFeedShop } from '@/hooks/market/useMarketShops';
import type { MarketFeedProduct } from '@/hooks/market/useMarketFeed';

const PAGE_SIZE = 20;

// ─── useSearchShops ───────────────────────────────────────────────────────

export function useSearchShops(name: string) {
  const [shops, setShops]         = useState<MarketFeedShop[]>([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [hasMore, setHasMore]     = useState(false);
  const [offset, setOffset]       = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const trimmedName = name.trim();

  const fetchPage = useCallback(async (targetOffset: number, replace: boolean) => {
    if (!trimmedName) {
      setShops([]);
      setHasMore(false);
      setOffset(0);
      return;
    }

    if (replace) setLoading(true); else setLoadingMore(true);
    setError(null);

    try {
      const token = await getValidToken();
      if (!token) {
        setError('session_expired');
        return;
      }

      const res = await fetch(`${API_BASE_URL}/market`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'search-shops',
          access_token: token,
          name:         trimmedName,
          limit:        PAGE_SIZE,
          offset:       targetOffset,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || 'Impossible de rechercher les boutiques');
        return;
      }

      setShops(prev => replace ? (data.shops || []) : [...prev, ...(data.shops || [])]);
      setOffset(data.next_offset ?? targetOffset);
      setHasMore(!!data.has_more);
    } catch (err) {
      console.error('[useSearchShops]', err);
      setError('network_error');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [trimmedName]);

  useEffect(() => {
    setShops([]);
    setOffset(0);
    setHasMore(false);
    fetchPage(0, true);
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    fetchPage(offset, false);
  }, [fetchPage, offset, hasMore, loadingMore]);

  return { shops, loading, loadingMore, error, hasMore, loadMore };
}

// ─── useSearchProducts ────────────────────────────────────────────────────

export function useSearchProducts(name: string, categoryId?: string) {
  const [products, setProducts]   = useState<MarketFeedProduct[]>([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [hasMore, setHasMore]     = useState(false);
  const [offset, setOffset]       = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const trimmedName = name.trim();
  const trimmedCategoryId = categoryId?.trim() || undefined;

  const fetchPage = useCallback(async (targetOffset: number, replace: boolean) => {
    if (!trimmedName) {
      setProducts([]);
      setHasMore(false);
      setOffset(0);
      return;
    }

    if (replace) setLoading(true); else setLoadingMore(true);
    setError(null);

    try {
      const token = await getValidToken();
      if (!token) {
        setError('session_expired');
        return;
      }

      const res = await fetch(`${API_BASE_URL}/market`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'search-products',
          access_token: token,
          name:         trimmedName,
          category_id:  trimmedCategoryId,
          limit:        PAGE_SIZE,
          offset:       targetOffset,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || 'Impossible de rechercher les produits');
        return;
      }

      setProducts(prev => replace ? (data.products || []) : [...prev, ...(data.products || [])]);
      setOffset(data.next_offset ?? targetOffset);
      setHasMore(!!data.has_more);
    } catch (err) {
      console.error('[useSearchProducts]', err);
      setError('network_error');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [trimmedName, trimmedCategoryId]);

  // Recharge depuis le début à chaque changement de terme OU de catégorie
  // — les deux filtres se combinent dans le même appel côté backend, donc
  // un changement de l'un ou l'autre invalide la page en cours.
  useEffect(() => {
    setProducts([]);
    setOffset(0);
    setHasMore(false);
    fetchPage(0, true);
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    fetchPage(offset, false);
  }, [fetchPage, offset, hasMore, loadingMore]);

  return { products, loading, loadingMore, error, hasMore, loadMore };
}
